import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PortalWithState } from 'react-portal';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import CustomerEditModal from '../Customer/CustomerEditModal';
import Button from '../Button/Button';
import Address from '../Address/Address';
import { useHistory } from 'react-router-dom';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { waterServiceStatusStyle } from './waterServiceStatus';
import useMode from '../../hooks/useMode';
import { TestResult } from '../../types/backflowTest';
import { Text } from 'rebass';
import DataTable from '../Table/DataTable';
import customerColumns from './WaterServiceCustomers.columns';
import assemblyColumns from './WaterServicesAssemblies.columns';
import AssemblyEditModal from '../BackflowAssembly/AssemblyEditModal';
import { PropertyType } from './PropertyTypePicker';
import { SourceType } from './SourceTypePicker';

export enum WaterServiceStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
}

export type WaterService = {
  id: string;
  waterSupplierId: string;
  siteNumber: string;
  meterNumber: string;
  meterSize?: number;
  pin: string;
  recycledWaterSite?: {
    id: string;
    name: string;
  };
  type: PropertyType;
  sourceType: SourceType;
  status: WaterServiceStatus;
  tags: string[];
  address: {
    id: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    zipExt?: string;
  };
  backflowAssemblies: {
    id: string;
    model: {
      name: string;
      backflowAssemblyMake: {
        name: string;
      };
    };
    size: string;
    serialNumber: string;
    testAnniversary: string;
    lastTestDate: string;
    lastTest?: {
      jobId: string;
      initialResult: TestResult;
      finalResult: TestResult;
    };
    lastNoticeSent?: {
      created: number;
    };
    status: string;
    testStatus: string;
    type: string;
    location: string;
    hazardType: string;
    hazardLevel: string;
  }[];
  serviceAddresses: ServiceAddress[];
  comments: WaterServiceComment[];
};

export type ServiceAddress = {
  id: string;
  customerId: string;
  customerRole: string;
  active: boolean;
  customer: {
    status: string;
    profile: {
      name: string;
    };
    accountNumber: string;
  };
  sendNotices: boolean;
};

export type WaterServiceComment = {
  id: string;
  profile: {
    id: string;
    name: string;
  };
  comment: string;
  created: number;
};

type WaterServiceDetailProps = {
  waterService: WaterService;
  onChange?: () => void;
};

const WaterServiceDetail: React.FC<WaterServiceDetailProps> = ({
  waterService,
  onChange,
}) => {
  const history = useHistory();
  const mode = useMode();

  const [assembyIds, setAssemblyIds] = useState(
    waterService ? waterService.backflowAssemblies.map((a) => a.id) : []
  );
  const [customerIds, setCustomerIds] = useState(
    waterService.serviceAddresses.map((customer) => customer.customerId)
  );

  useEffect(() => {
    setAssemblyIds(waterService.backflowAssemblies.map((a: any) => a.id));
    setCustomerIds(
      waterService.serviceAddresses.map((customer) => customer.customerId)
    );
  }, [waterService]);

  const customers = useMemo<ServiceAddress[]>(() => {
    const indexed = waterService.serviceAddresses.reduce(
      (indexed, serviceAddress) => {
        indexed[serviceAddress.customerId] = serviceAddress;
        return indexed;
      },
      {} as Record<string, ServiceAddress>
    );

    return Object.keys(indexed)
      .map((key) => indexed[key])
      .sort((a, b) => {
        const aPoints = +a.active + +(a.customer?.status === 'ACTIVE');
        const bPoints = +b.active + +(b.customer?.status === 'ACTIVE');
        return bPoints - aPoints;
      });
  }, [waterService.serviceAddresses]);

  const assemblies = useMemo(() => {
    return waterService.backflowAssemblies.slice().sort((a, b) => {
      return +(b.status === 'ACTIVE') - +(a.status === 'ACTIVE');
    });
  }, [waterService.backflowAssemblies]);

  return (
    <>
      <Accordion title="Details">
        <AccordionItem label="Service" value={waterService.siteNumber} />
        <AccordionItem
          label="Meter"
          value={
            (waterService.meterSize ? `${waterService.meterSize}" ` : '') +
            waterService.meterNumber
          }
        />
        <AccordionItem label="TRAC" value={waterService.pin} />
        {waterService.recycledWaterSite && (
          <AccordionItem
            kind="link"
            label="Site"
            value={waterService.recycledWaterSite.name}
            href={`/${mode}/water-sites/${waterService.recycledWaterSite.id}`}
          />
        )}
        {waterService.type && (
          <AccordionItem
            label="Property Type"
            value={constantToLabel(waterService.type)}
          />
        )}
        <AccordionItem
          label="Source Type"
          value={constantToLabel(waterService.sourceType)}
        />
        <AccordionItem
          kind="chip"
          status={waterServiceStatusStyle(waterService.status)}
          label="Status"
          value={constantToLabel(waterService.status)}
        />
        <AccordionItem
          label="Address"
          value={<Address address={waterService.address} />}
        />
      </Accordion>
      <Accordion
        title="Customers"
        icon={
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button variant="primary" icon="plus" onClick={openPortal}>
                  Customer
                </Button>
                {portal(
                  //this From<odal needs to be adjusted!!!
                  <CustomerEditModal
                    action="add"
                    customer={{
                      profile: {
                        contactAddressId: waterService.address.id,
                      },
                    }}
                    waterServiceId={waterService.id}
                    onClose={() => {
                      closePortal();
                      if (onChange) {
                        onChange();
                      }
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        }
      >
        {customers && customers.length ? (
          <DataTable
            columns={customerColumns}
            itemIds={customerIds}
            onRowClick={(item: WaterService['serviceAddresses'][number]) => {
              history.push(`/${mode}/customers/${item.customerId}`);
            }}
            items={customers}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no customers assigned to this location.
          </Text>
        )}
      </Accordion>
      <Accordion
        title="Assemblies"
        icon={
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button variant="primary" icon="plus" onClick={openPortal}>
                  Assembly
                </Button>
                {portal(
                  <AssemblyEditModal
                    action="create"
                    assembly={{
                      addressId: waterService.address.id,
                      waterSupplierId: waterService.waterSupplierId,
                      waterServiceId: waterService.id,
                    }}
                    onClose={() => {
                      closePortal();
                      if (onChange) {
                        onChange();
                      }
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        }
      >
        {assemblies && assemblies.length ? (
          <DataTable
            columns={assemblyColumns}
            itemIds={assembyIds}
            items={assemblies}
            onRowClick={(item: WaterService['backflowAssemblies'][number]) => {
              history.push(`/${mode}/assemblies/${item.id}`);
            }}
            allowSelection={true}
          />
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no assemblies at this location.
          </Text>
        )}
      </Accordion>
    </>
  );
};

WaterServiceDetail.propTypes = {
  waterService: PropTypes.any,
};

export default WaterServiceDetail;
